import { parse, stringify } from 'yaml'

export type AgentMemoryScope = 'none' | 'project'

export interface AgentYamlConfig {
  systemPrompt: string
  model: string
  effort: string | null
  memory: AgentMemoryScope
  skills: string[]
  mcps: string[]
  tools: string[]
}

export const AGENT_YAML_MAX = 4000
export const AGENT_NAME_MAX = 256
export const AGENT_DESCRIPTION_MAX = 2000

export const AGENT_STARTER_YAML = `systemPrompt: |
  Describe what this agent does and how it should behave.
model: sonnet-4-6
effort: medium
memory: none
skills: []
tools: []
`

export const MANAGED_PROMPT_PREFIX = 'refs/prompts/'

const EXPRESSION_START = '${{'
const EXPRESSION_END = '}}'

/** Must match AgentPromptRef.cs; divergence makes the UI describe a materialization the server won't do. */
export function tryGetManagedPromptName(systemPrompt: string | null | undefined): string | null {
  if (!systemPrompt?.trim()) return null

  let candidate = systemPrompt.trim()
  if (candidate.startsWith(EXPRESSION_START) && candidate.endsWith(EXPRESSION_END)) {
    candidate = candidate.slice(EXPRESSION_START.length, -EXPRESSION_END.length).trim()
  }

  if (!candidate.startsWith(MANAGED_PROMPT_PREFIX)) return null
  if (/\s/.test(candidate)) return null

  return candidate.slice(MANAGED_PROMPT_PREFIX.length)
}

const KNOWN_KEYS = new Set(['systemPrompt', 'model', 'effort', 'memory', 'skills', 'mcps', 'tools'])

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .filter(item => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
    .map(String)
}

function scalarString(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

export function listUnknownAgentYamlKeys(yamlContent: string): string[] {
  let raw: unknown
  try {
    raw = parse(yamlContent)
  } catch {
    return []
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return []
  return Object.keys(raw).filter(key => !KNOWN_KEYS.has(key))
}

export function parseAgentYaml(yamlContent: string): AgentYamlConfig {
  const raw: unknown = parse(yamlContent)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Agent YAML must be a mapping.')
  }
  const obj = raw as Record<string, unknown>

  const mcps = Array.isArray(obj.mcps)
    ? obj.mcps
        .map(entry => {
          if (typeof entry === 'string') return entry
          if (entry && typeof entry === 'object' && typeof (entry as Record<string, unknown>).connectorRef === 'string') {
            return (entry as { connectorRef: string }).connectorRef
          }
          return null
        })
        .filter((ref): ref is string => !!ref)
    : []

  return {
    systemPrompt: scalarString(obj.systemPrompt),
    model: scalarString(obj.model),
    effort: scalarString(obj.effort) || null,
    memory: scalarString(obj.memory).trim().toLowerCase() === 'project' ? 'project' : 'none',
    skills: stringArray(obj.skills),
    mcps,
    tools: stringArray(obj.tools),
  }
}

export function declaresProjectMemory(yamlContent: string): boolean {
  try {
    return parseAgentYaml(yamlContent).memory === 'project'
  } catch {
    return false
  }
}

export function serializeAgentYaml(config: AgentYamlConfig): string {
  const doc: Record<string, unknown> = {
    systemPrompt: config.systemPrompt,
    model: config.model,
  }
  if (config.effort) doc.effort = config.effort
  doc.memory = config.memory
  doc.skills = config.skills
  doc.mcps = config.mcps.map(connectorRef => ({ connectorRef }))
  doc.tools = config.tools
  return stringify(doc)
}
